'use client'

import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { testimonials as testimonialData } from 'src/utils/data'
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
// Import correct types for Swiper 8.x
import SwiperCore, {
  Autoplay,
  EffectCoverflow,
  Navigation,
  Pagination
} from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import TestimonialCard from './Testimonial/card'

// Install Swiper modules
SwiperCore.use([EffectCoverflow, Autoplay, Navigation, Pagination])

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [mounted, setMounted] = useState(false)
  const swiperInstanceRef = useRef<any>(null)
  const autoplayDirectionRef = useRef(false) // false = forward, true = reverse

  useEffect(() => {
    setMounted(true)
  }, [])

  // Reset swiper when direction changes or component unmounts
  useEffect(() => {
    return () => {
      // Clean up swiper instance on unmount if needed
      if (swiperInstanceRef.current) {
        // For Swiper 8, no explicit destroy needed as React handles cleanup
        swiperInstanceRef.current = null
      }
    }
  }, [])

  if (!mounted) return null

  return (
    <motion.section
      data-item="testimonials-section"
      className="py-10 md:py-16 lg:py-24 bg-white relative mb-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}>
      <div className="container mx-auto px-4 md:px-8">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 md:mb-12 text-center text-gray-900"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}>
          Why top restaurant ops teams rely on
          <br className="hidden md:block" /> Loop's Delivery Intelligence
        </motion.h2>

        <div className="w-full h-full relative overflow-x-hidden">
          <Swiper
            // Force re-render on direction change with this compound key
            key={`testimonials-${testimonialData.length}-${autoplayDirectionRef.current}`}
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            loop={true}
            loopAdditionalSlides={2}
            speed={500}
            slidesPerView="auto"
            slidesPerGroup={1}
            watchSlidesProgress={true}
            updateOnImagesReady={true}
            observer={true}
            observeParents={true}
            observeSlideChildren={true}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
              reverseDirection: false
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true
            }}
            navigation={{
              enabled: true,
              prevEl: '.swiper-button-prev',
              nextEl: '.swiper-button-next'
            }}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 100,
              modifier: 1.5,
              slideShadows: false
            }}
            onSwiper={(swiper) => {
              swiperInstanceRef.current = swiper
              // Store direction value directly without accessing autoplay
              autoplayDirectionRef.current = false // Default to false since we set it that way

              // For Swiper 8.x - manually ensure proper initialization
              swiper.update()
              setTimeout(() => {
                // Additional update after DOM is fully rendered
                swiper.update()
              }, 100)
            }}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.realIndex)
            }}
            className="testimonial-swiper !overflow-visible">
            <div className="swiper-button-prev sm:!flex"></div>
            <div className="swiper-button-next sm:!flex"></div>
            {testimonialData.map((testimonial, idx: number) => (
              <SwiperSlide
                key={`slide-${idx}`}
                className="!w-[340px] md:!h-[620px] h-auto"
                style={{ minHeight: '400px' }}>
                <div className="h-full w-full">
                  <TestimonialCard
                    testimonial={testimonial}
                    idx={idx}
                    activeIndex={activeIndex}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <style jsx global>{`
        .testimonial-swiper {
          padding: 50px 0;
          overflow: visible !important;
          width: 100%;
          height: auto;
          min-height: 400px;
        }
        .swiper-slide {
          transition: all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
          overflow: visible !important;
          backface-visibility: hidden;
          transform-style: preserve-3d;
          will-change: transform;
          height: auto;
        }
        .swiper-slide-active {
          z-index: 2;
        }
        .swiper-wrapper {
          transition-timing-function: cubic-bezier(
            0.25,
            0.1,
            0.25,
            1
          ) !important;
          min-height: 400px;
          align-items: center;
        }
        .swiper-button-next,
        .swiper-button-prev {
          color: #196e82;
          background: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          transition:
            background 0.3s ease,
            transform 0.3s ease;
        }
        .swiper-button-next:hover,
        .swiper-button-prev:hover {
          background: #f3f4f6;
          transform: scale(1.1);
        }
        .swiper-button-next:after,
        .swiper-button-prev:after {
          font-size: 18px;
          font-weight: bold;
        }
        .swiper-pagination-bullet {
          transition: all 0.3s ease;
        }
        .swiper-pagination-bullet-active {
          transform: scale(1.3);
        }

        @media (max-width: 768px) {
          .swiper-pagination {
            bottom: 10px !important;
          }
          .testimonial-swiper {
            padding: 0 0 40px 0;
            margin: 0;
          }
          .swiper-slide {
            margin: 0 -10px;
          }
        }
      `}</style>

      <div
        className="absolute -bottom-10 left-0 right-0 w-full h-[72px] bg-repeat-x -z-0"
        style={{
          backgroundImage:
            'url(/v2/images/deliveryIntelligenceSectionBackdrop.svg)',
          backgroundPosition: 'center bottom'
        }}
      />
    </motion.section>
  )
}

export default TestimonialsSection
